/**
 * Query scope for the All inquiry queue.
 *
 * Separate from `InquiryTab` so the tab file exports only its component, and so
 * the filter string is testable without mounting a table.
 */

import dayjs from 'dayjs';
import {
  BariatricProcedureStage,
  BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM,
  PATIENT_FILTER_TAG_SYSTEMS,
} from '@bariendo/lib/constants';
import { inquiryWindowStart } from '@bariendo/lib/pac-inquiry-list';
import { ChipFilterDefinition } from '@/components/FilterChips/useChipFilters';
import { dayTagClause, ListRange } from '@/components/Hub/listRange';
import { InquiryLeadRow } from './inquiryRows';
import { patientFilterClauses, QueueFilterValues } from './queueFilters';

/**
 * Every stage a patient can hold that is NOT Inquiry. Excluded by `_tag ne`
 * rather than including Inquiry by `_tag eq`, so a brand-new sign-up the stage
 * bot has not tagged yet is still in the candidate set — the bot runs on
 * payment and appointment webhooks, not on sign-up, so the freshest leads are
 * exactly the untagged ones.
 */
const STAGES_PAST_INQUIRY = Object.values(BariatricProcedureStage).filter(
  (stage) => stage !== BariatricProcedureStage.PRE_CONSULTATION_UNPAID && stage !== BariatricProcedureStage.UNKNOWN
);

/**
 * The seven-day window, server-side, rooted on the Patient.
 *
 * A self-pay sign-up creates a Patient and nothing else — no `EpisodeOfCare`
 * (that is a partner-track enrolment), no stage tag, no appointment — so the
 * only server-searchable trace of "signed up this week" is that the Patient was
 * written this week. `_lastUpdated ge` is therefore a superset: it also catches
 * older Inquiry leads someone edited or re-staged this week. `inquiryRows`
 * narrows to the real sign-ups by sign-up date.
 */
export function buildInquiryPatientFilter(now: Date): string {
  const parts = [
    `_lastUpdated ge "${inquiryWindowStart(now)}"`,
    ...STAGES_PAST_INQUIRY.map((stage) => `_tag ne "${BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM}|${stage}"`),
  ];

  return `(${parts.join(' and ')})`;
}

/**
 * The Intake hub's scope, all on the server (BAR-2877): the Inquiry queue tag,
 * the Signed up preset as `signup-day` tags, and the four filters on the
 * Patient's own tags (2.1a). Sorted in the browser: tags can't sort.
 * `inquiryRows` trims the UTC-day edges to the exact signup date.
 */
export function buildInquiryHubFilter(range: ListRange, values: QueueFilterValues): string {
  const signedUp = dayTagClause(PATIENT_FILTER_TAG_SYSTEMS.signupDay, range);
  const parts = [
    `_tag eq "${PATIENT_FILTER_TAG_SYSTEMS.queue}|inquiry"`,
    ...(signedUp ? [signedUp] : []),
    ...patientFilterClauses(values, ''),
  ];

  return `(${parts.join(' and ')})`;
}

/** The hub's subscription: anything written from today on, whatever the preset. */
export function inquiryHubSubscriptionParams(today: Date): Record<string, string> {
  return { _lastUpdated: `ge${dayjs(today).format('YYYY-MM-DD')}` };
}

/**
 * What the tab subscribes to. The query's `_filter` cannot be a Subscription
 * criteria (the matcher fails it silently — see `useLiveGraphQL`), so this is
 * the one plain parameter the matcher can evaluate: anything written since the
 * window opened. `inquiryRows` narrows what arrives the same way it narrows the
 * page — by stage and by sign-up date.
 */
export function inquirySubscriptionParams(now: Date): Record<string, string> {
  return { _lastUpdated: `ge${inquiryWindowStart(now)}` };
}

/**
 * Two chips, one per coverage conversation rather than a financing one: leads
 * whose payment is an employer benefit, and leads on their own insurance. The
 * two never share a row, because insurance is not employer coverage.
 */
export const INQUIRY_CHIPS: ChipFilterDefinition<InquiryLeadRow>[] = [
  {
    id: 'employer-benefit',
    label: 'Employer benefit',
    predicate: (row) => row.employerBenefit,
  },
  {
    id: 'insurance',
    label: 'Insurance',
    predicate: (row) => row.insurance,
  },
];
